import type { MultiComboboxOption } from '@/components/ui';
import type { ApiKeyScope } from '@/lib/types';

/**
 * The ONE decision point for API-key management: the scope vocabulary as data,
 * the two payload rules that decide what a save actually sends, and the three
 * sentences this feature is not allowed to lose.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES.** The page and the form own their own
 * requests; this module answers *given what the server said and what the reader
 * has done, what may the screen say and what may it send?* A hook added here
 * would put a second copy of the scopes rule behind a request, and the rule is
 * the whole point.
 *
 * ⚠️ **It lives under `components/api-keys/` rather than in `src/lib/` because
 * that is the directory this feature owns.** If a second surface outside this
 * folder ever needs the scopes rule, MOVE it to `src/lib/` — do not copy it. A
 * second copy would agree the day it was written and drift on the first
 * correction, and what would drift is which capabilities a live credential
 * carries.
 */

/* ── THE THREE PROTECTED SENTENCES ─────────────────────────────────────────
 *
 * ⛔ **A HELPER-TEXT SWEEP MUST NOT DELETE ANY OF THESE.** The house rule
 * forbids prose attached to a CONTROL that competes with the control. Each of
 * these names a state the reader cannot otherwise see, which is the documented
 * exception:
 *
 *   - the token warning names an irreversible consequence of closing a dialog;
 *   - the revoke sentence names what a destructive action actually does, and
 *     what it deliberately does NOT do;
 *   - the rate-limit sentence is the only thing that can tell an empty box from
 *     a zero, and those are opposite instructions.
 *
 * The test to apply before removing any sentence in this feature: **what wrong
 * conclusion does it prevent?** If the answer is "none", it goes.
 */

/**
 * Said at the moment the plaintext is on screen, and nowhere else.
 *
 * ⛔ The claim is literal, not cautionary. Only a sha256 and a 12-character
 * prefix are stored, so there is no recovery path anywhere in SEED EMERGE, in
 * the database, or through a support request — the only way back is a new key.
 * Softening it to "make sure you copy it" would read as a nag, and a nag is
 * what people close.
 */
export const TOKEN_SHOWN_ONCE =
  'This is the only time SEED EMERGE will show this token. Only a hash of it is stored, ' +
  'so it cannot be recovered or shown again. If it is lost, revoke this key and issue a new one.';

/**
 * The revoke confirmation, in full.
 *
 * ⛔ **IT PROMISES NO UNDO, IN ANY FORM.** `revokedAt` is set once and never
 * cleared, there is no un-revoke endpoint, and the plaintext is gone — so "you
 * can turn it back on" would be a promise about a value that no longer exists.
 * It says what happens and stops.
 *
 * ⚠️ **It says revoking is not deleting, because the row surviving is the
 * point rather than a detail.** The row, its name, its prefix and its scopes
 * are all kept so `f_api_logs` can still resolve what a key was allowed to do
 * after it stops working. A reader who expects the row to disappear and then
 * sees it still listed reads the control as having failed and presses it again.
 */
export const REVOKE_CONSEQUENCE =
  'Any caller still using this key stops working immediately. Revoking is not deleting — the ' +
  'key keeps its row, so the audit trail still resolves which key made past calls. There is no ' +
  'un-revoke, and the token cannot be recovered: restoring access means issuing a new key and ' +
  'updating whatever was using this one.';

/**
 * The rate-limit box, and the one fact an empty box cannot state for itself.
 *
 * 🔴 **BLANK AND ZERO ARE OPPOSITE INSTRUCTIONS AND LOOK ALIKE ON SCREEN.**
 * Blank falls back to the global budget. `0` is the value most likely to be
 * typed by somebody who means *no limit*, and it means the exact opposite.
 *
 * ⚠️ **It does not promise "unlimited" either, and that wording is forbidden**
 * — the global limiter still applies to a key with no per-key budget, so
 * "unlimited" would be a claim about the system that nothing here can express.
 */
export const RATE_LIMIT_BLANK_MEANS_GLOBAL =
  'Blank means the global budget. It does not mean zero.';

/**
 * Why a typed `0` is refused.
 *
 * ⚠️ **THIS MIRRORS A SERVER REFUSAL AND DOES NOT INVENT ONE.** The API rejects
 * `rateLimitPerMinute: 0`, so refusing it here saves a round trip rather than
 * imposing a convention — and it names the way to say what the person almost
 * certainly meant, which the server's own message cannot.
 */
export const RATE_LIMIT_ZERO_REFUSED =
  'A rate limit of 0 is refused — it would be a key that can make no requests. Leave the box ' +
  'blank to use the global budget.';

/* ── THE SCOPE VOCABULARY ──────────────────────────────────────────────────*/

/**
 * The scopes, as data.
 *
 * ⚠️ **`ApiKeyScope` is a TYPE and carries no runtime array**, deliberately —
 * `types.ts` exports types and nothing else. This is that union as options, and
 * the two are kept in step **deliberately rather than by coincidence**: adding a
 * member to the union without adding it here leaves a scope the server accepts
 * and no screen can grant.
 *
 * ⚠️ **THE LABEL IS THE SCOPE STRING ITSELF, deliberately.** A super-admin
 * configuring a credential is matching what somebody else's integration was told
 * to expect, so the literal is the identifying text. The plain-English reading
 * goes in the option `hint`, which is a property of the OPTION rather than prose
 * attached to a control, and is therefore not touched by the no-helper-text rule.
 *
 * ⛔ **A SCOPE IS THE CAPABILITY AND `purpose` IS NOT**, however much `purpose`
 * reads like one. Nothing may branch on `purpose`.
 */
export const SCOPE_CATALOGUE: readonly (MultiComboboxOption & { value: ApiKeyScope })[] = [
  {
    value: 'companies.list.public',
    label: 'companies.list.public',
    hint: 'Read the lean public company list — id, name, short name, country and approval status',
  },
  /*
   * 🔴 IMMEDIATELY BELOW THE ROUTE IT EXTENDS, FOR THE SAME REASON THE BLOCK
   * BELOW GIVES: this is the only entry in the catalogue that adds a FIELD to a
   * route rather than opening one, so read on its own it is unintelligible.
   * Above `companies.read` as well as below `companies.list.public`, so the
   * three "companies" scopes read as a ladder — one route, one column more, the
   * whole row — which is the comparison somebody ticking a box has to make.
   *
   * ⚠️ ITS HINT SAYS WHAT A KEY WITHOUT IT SEES, not what a key with it gains,
   * because the absence is the half nobody can observe: the route does not
   * refuse an unscoped key, it answers without the field.
   */
  {
    value: 'companies.currency.read',
    label: 'companies.currency.read',
    hint: 'Adds the currency to that same list. Without it the field is absent, not empty',
  },
  /*
   * 🔴 PLACED HERE RATHER THAN APPENDED, AND THE ADJACENCY IS THE MITIGATION.
   * This list is otherwise in the order scopes were added, so a reader tidying it
   * would move this one to the end — don't. `companies.read` and
   * `companies.list.public` are one segment apart in the name and a whole company
   * row apart in what they grant, and this is a picker somebody TICKS: separated,
   * a person scanning for "companies" finds one, grants it, and never learns the
   * other existed. Adjacent, with hints that contrast, the choice has to be made
   * rather than stumbled into.
   *
   * ⚠️ The backend draws the same contrast BY NAME at `GET /public/companies` —
   * "the narrowest true description of what it grants — `companies.list.public`,
   * not `companies.read`" — so the confusability was foreseen there before either
   * name reached a screen here.
   */
  {
    value: 'companies.read',
    label: 'companies.read',
    hint: 'Read the FULL company row — every field, not the lean projection above',
  },
  {
    value: 'companies.create',
    label: 'companies.create',
    hint: 'Create a company. It lands pending and raises an approval round',
  },
  {
    value: 'event-interest.submit',
    label: 'event-interest.submit',
    hint: 'Submit an event interest form response',
  },
  /*
   * 🔴 THESE TWO WERE MISSING UNTIL 2026-09-18, AND THE WARNING ABOVE IS EXACTLY
   * WHAT HAPPENED — from the other direction. The server added both; neither this
   * list nor `ApiKeyScope` moved. So this picker offered three options, a
   * five-scope key was NOT EXPRESSIBLE from any screen in this app, and the live
   * "Event Interest Form" key went out carrying three.
   *
   * ⚠️ IT WAS FOUND BY A PERSON OPENING THE DIALOG AND COUNTING, not by any
   * instrument. `tsc` was green — the union agreed with the list, both being
   * short together — and the screen renders a perfectly good picker. A client
   * that ENUMERATES somebody else's vocabulary cannot tell "complete" from
   * "stale", and the tell is not in this repo at all.
   *
   * ⛔ So the check is comparing this array against `API_KEY_SCOPES` in the
   * backend, and nothing less. Re-run it whenever a route starts requiring a
   * scope; a missing entry here is a capability nobody can grant.
   */
  {
    value: 'event-interest.festivals.read',
    label: 'event-interest.festivals.read',
    hint: 'Read the public festival list the 2027 form offers',
  },
  {
    value: 'countries.list',
    label: 'countries.list',
    hint: 'Read the countries list — the form needs it for its country picker',
  },
  /*
   * ── THE FUTURE-BILLABLES SCOPES, 2026-09-18 ───────────────────────────────
   *
   * Added so another team's integration can present a key rather than signing in
   * as a person.
   *
   * 🔴 `invoice-tracker.create` CARRIES A CONSEQUENCE, AND ITS HINT STATES IT
   * RATHER THAN THIS COMMENT ALONE. `POST /invoice-tracker` answers 201, and a
   * standalone row has NO NATURAL KEY — the row IS the record — so the route
   * carries no idempotency key and the same body sent twice is two rows and two
   * 201s. The sibling PUT is idempotent precisely because an ATTACHED row's
   * natural key is the DOCUMENT; this one has nothing to be idempotent against.
   * So a retried or double-fired call leaves a second planned billable sitting on
   * finance's tracker, and nothing about the second 201 says it is a duplicate.
   *
   * ⛔ AND THE KEY'S OWN USER CANNOT UNDO IT. Create and update are `WRITE_ROLES`;
   * DELETE is `ADMIN_ROLES`. The integration can therefore MAKE the duplicate and
   * cannot REMOVE it — an EMERGE administrator has to. That asymmetry is what
   * earns this a sentence at the tick instead of a line in a contract document:
   * the person granting the scope is not the person who will clear up after it.
   *
   * ⚠️ THIS IS A CONSEQUENCE BESIDE A CONTROL, WHICH IS THE DOCUMENTED EXCEPTION
   * TO THE NO-HELPER-TEXT PROHIBITION — the same standing as the token warning and
   * the revoke sentence at the top of this file. It names a state the reader
   * cannot otherwise see, and the wrong conclusion it prevents is *"a call that
   * failed can simply be retried"*. ⛔ A HELPER-TEXT SWEEP MUST NOT DELETE IT.
   * (It is an option `hint` besides — a property of the OPTION, which that rule
   * has never covered — so removing it would be a second mistake, not one.)
   */
  {
    value: 'invoice-tracker.read',
    label: 'invoice-tracker.read',
    hint: 'List future billables — the planned work finance expects to raise',
  },
  {
    value: 'invoice-tracker.create',
    label: 'invoice-tracker.create',
    hint: 'Raise one. No idempotency key, so a retry leaves a SECOND billable row',
  },
  {
    value: 'invoice-tracker.update',
    label: 'invoice-tracker.update',
    hint: 'Amend a future billable that already exists',
  },
  /*
   * 🔴 SUBMIT ONLY. IT REACHES NO READ, AND THAT IS STRUCTURAL RATHER THAN A
   * SCOPE THAT HAS NOT BEEN GRANTED YET.
   *
   * The two staff reads on this module carry `denyApiKey` AND a
   * `b2b-proposal.read` permission check in the service, so **a key cannot
   * reach either however its scopes are ticked**. Do not word this as
   * "manage" or "access" proposals: a super administrator reading that would
   * reasonably believe ticking it exposed customer contact details, and
   * refuse a grant that is in fact write-only.
   *
   * ⚠️ THIS ENTRY IS WHY THE BOX EXISTS AT ALL, AND THE UNION IS NOT ENOUGH.
   * `SCOPE_CATALOGUE` is an ARRAY, not an exhaustive `Record<ApiKeyScope, …>`,
   * so widening `ApiKeyScope` alone leaves `tsc` GREEN and the scope offered
   * by nothing — measured 2026-09-20, not predicted. `scopeOptions()` does not
   * rescue it either: it injects an unknown scope only when the key ALREADY
   * HOLDS one, which protects a whole-set replace from revoking something
   * unrecognised and is no route to GRANTING it.
   *
   * ⛔ SO THE TWO FILES MOVE TOGETHER OR NEITHER MOVES. A scope in the union
   * and not here is a live server capability no screen can confer.
   */
  {
    value: 'b2b-proposal.submit',
    label: 'b2b-proposal.submit',
    hint: 'Submit a B2B "Get In Touch" proposal — submit only, never read',
  },
  /*
   * SUBMIT ONLY, the same shape as the proposal scope above: the staff reads on
   * `/report-download/leads` carry `denyApiKey` and a `report-download.read`
   * permission, so no key reaches them however its scopes are ticked.
   *
   * ⛔ MOVES WITH THE `ApiKeyScope` MEMBER OR NOT AT ALL — this is an array, so
   * `tsc` cannot see a missing entry.
   */
  {
    value: 'report-download.submit',
    label: 'report-download.submit',
    hint: 'Submit a report-download lead — submit only, never read',
  },
  {
    value: 'products.read',
    label: 'products.read',
    hint: 'List products — full catalogue rows, including prices',
  },
  {
    value: 'projects.read',
    label: 'projects.read',
    hint: 'List projects — full project rows',
  },
];

const KNOWN_SCOPES = new Set<string>(SCOPE_CATALOGUE.map((option) => option.value));

/**
 * Said against a scope the key holds that this build does not know about.
 *
 * ⚠️ **NOT "retired" and not "invalid".** Either would be a claim about the
 * server this client cannot make: the likeliest reason for an unknown scope is
 * that the vocabulary grew and this bundle has not caught up.
 */
export const UNKNOWN_SCOPE_HINT = 'Held by this key and not in this version of SEED EMERGE';

/**
 * The options a scopes picker offers: the catalogue, plus every scope the key
 * ALREADY holds that the catalogue does not name.
 *
 * 🔴 **THE INJECTION IS WHAT STOPS AN UNKNOWN SCOPE BEING SILENTLY DESTROYED,
 * AND IT IS NOT DEFENSIVE.** `ApiKeyScope` is a closed union in THIS bundle;
 * the column behind it is a `VARCHAR(64)` with no enum, so a deployed server can
 * answer with a scope added after this build shipped. A picker offering only the
 * three known scopes would drop that value from its selected set the moment it
 * rendered — and because a scopes write is a WHOLE-SET REPLACE, the next save,
 * made for any reason at all, would send a set that no longer contains it and
 * **revoke a capability nobody looked at**, with a `200` and nothing on screen.
 *
 * This is the treatment `withStoredOption()` gives a retired reference value
 * everywhere else in this app, reached from the other direction: there the
 * stored value is one the master no longer offers, here it is one this bundle
 * never knew.
 *
 * ⚠️ **The parameter is `readonly string[]` rather than `ApiKeyScope[]` on
 * purpose** — the whole point is the value that is NOT in the union.
 */
export function scopeOptions(selected: readonly string[]): MultiComboboxOption[] {
  const extra = selected
    .filter((scope) => !KNOWN_SCOPES.has(scope))
    .map((scope) => ({ value: scope, label: scope, hint: UNKNOWN_SCOPE_HINT }));
  return [...SCOPE_CATALOGUE, ...extra];
}

/**
 * The picker's `string[]` as the wire's `ApiKeyScope[]`.
 *
 * 🔴 **A CAST, AND A FILTER HERE WOULD BE STRICTLY WORSE — that is the whole
 * reason this function exists rather than an inline `.filter()`.** The obvious
 * "safe" version keeps only values in the union, and that is precisely how the
 * unknown scope {@link scopeOptions} exists to preserve gets dropped: it would
 * pass the picker, fail the filter, and vanish from a whole-set replace. The
 * cast is sound **by construction** — every value this control can emit is
 * either a catalogue scope or one the key already held, because those are the
 * only options it is given.
 *
 * ⚠️ **So the guard is the OPTION LIST, not this function.** If a caller ever
 * feeds this from anywhere other than a picker built by `scopeOptions`, the
 * argument above stops holding and the value has to be checked at that source.
 */
export function asScopes(values: readonly string[]): ApiKeyScope[] {
  return [...values] as ApiKeyScope[];
}

/* ── THE TWO PAYLOAD RULES ─────────────────────────────────────────────────*/

/**
 * Has the scope SET moved?
 *
 * 🔴 **THIS IS THE WHOLE OF "AN UNTOUCHED SCOPES CONTROL SENDS NOTHING".**
 * A scopes write REPLACES the whole set, so a payload builder has three states
 * to keep apart:
 *
 * | sent | means |
 * | --- | --- |
 * | absent | leave the stored scopes alone |
 * | `[]` | **CLEAR every scope** — a real instruction, and a valid state |
 * | `[…]` | these, and only these |
 *
 * ⛔ **`[]` DESTROYS, ABSENT LEAVES ALONE** — one falsy value carrying two
 * opposite instructions, one keystroke apart, and **an empty scope set is legal,
 * so there is no refusal to catch the mistake.** The key is therefore emitted
 * only when this returns `true`, and an untouched control can never produce one.
 * ⛔ **The caller must not "helpfully" re-send the unchanged set either**: that
 * re-asserts a set assembled from options this bundle may not know in full,
 * which is how the hazard in {@link scopeOptions} arrives through the back door.
 *
 * ⚠️ **COMPARED BY CONTENT, ORDER-INDEPENDENTLY.** The server returns scopes in
 * its own order and a picker holds them in the order somebody ticked; comparing
 * as sequences would report every key as edited the moment the two disagreed,
 * and the form would then send a whole-set replace on a visit that changed
 * nothing at all.
 */
export function scopesChanged(baseline: readonly string[], next: readonly string[]): boolean {
  if (baseline.length !== next.length) return true;
  const have = new Set(baseline);
  return next.some((scope) => !have.has(scope));
}

/**
 * The rate limit as the wire wants it: a number, or `null` for the global
 * budget.
 *
 * 🔴 **BLANK RESOLVES TO `null` HERE, NEVER TO `0`, AND THE REFUSAL OF THAT
 * READING IS IN THE BUILDER RATHER THAN ONLY IN THE LABEL.** `Number('')` is
 * `0`, so the obvious one-liner — `Number(raw.trim())` — turns "use the global
 * budget" into a value the server refuses, on a control whose whole job is to
 * tell those two apart. A label alone does not close it: the label is read by
 * the person typing and the coercion is performed by the code.
 *
 * ⚠️ Anything {@link rateLimitError} would refuse answers `null` rather than
 * `NaN`, so a bad value can never travel as a number.
 */
export function rateLimitPayload(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (value === 0 || value > MAX_RATE_LIMIT) return null;
  return value;
}

/** `INT UNSIGNED`. Above this the server refuses, so the control stops here. */
export const MAX_RATE_LIMIT = 4294967295;

/**
 * Why the typed rate limit cannot be sent, or `null` when it can.
 *
 * ⚠️ **Blank is VALID and is the commonest answer.** It is checked first, so the
 * empty case can never fall into the numeric branch and collect a message about
 * a number nobody typed.
 */
export function rateLimitError(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'Use a whole number of requests per minute, or leave it blank.';
  if (Number(trimmed) === 0) return RATE_LIMIT_ZERO_REFUSED;
  if (Number(trimmed) > MAX_RATE_LIMIT) return `Must be ${MAX_RATE_LIMIT} or lower.`;
  return null;
}

/** Server-side maximums, mirrored so a control stops where the API would. */
export const MAX_LENGTHS = {
  name: 100,
  purpose: 64,
} as const;

/**
 * How the rate limit reads in a table cell.
 *
 * ⚠️ **`null` prints the global budget in words and NEVER a dash.** An em dash
 * in this column would read as "no limit recorded", which is the one thing it
 * does not mean — the fallback is a real limit, and it is not unlimited.
 */
export function rateLimitLabel(limit: number | null): string {
  if (limit === null) return 'Global budget';
  return `${limit}/min`;
}
